import type {
  LocationQuery,
} from 'vue-router';
import { isString } from 'lodash-es';
import { defineStore } from 'pinia';
import {
  computed,
  ref,
  watch,
} from 'vue';

import type { IAppConfigMerchant } from '@modules/merchant/types';
import { useConfig } from '@modules/config/composables';
import { useBrowserTitle } from '@modules/core/composables/browser-title';
import setThemeColors from '@modules/core/utils/setThemeColors';
import { useCurrentNamespace } from '@modules/permissions/composables';

import useMerchantIdStore from './useMerchantIdStore';

const useMerchantStore = defineStore('merchant', () => {
  const { setMerchantId } = useMerchantIdStore();
  const { config } = useConfig();
  const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();
  const { setTitle } = useBrowserTitle();

  const merchant = ref<IAppConfigMerchant>();
  const merchantPreview = ref<IAppConfigMerchant>();

  const currentMerchant = computed(() => merchantPreview.value ?? merchant.value);

  const merchantList = computed<IAppConfigMerchant[]>(() => config.value?.merchants_list ?? []);

  const browserTitle = computed(() => (
    [
      config.value?.title,
      isAdminNamespace.value || isAffiliateNamespace.value ? currentMerchant.value?.merchant_name : null,
    ].filter((item) => !!item).join(' • ')));

  const findMerchantByValue = (value: string): IAppConfigMerchant | undefined => {
    const merchantId = Number(value);

    return merchantList.value.find((item) => (Number.isNaN(merchantId) ? item.merchant_name === value : item.id === merchantId));
  };

  const updateMerchantPreview = (item: IAppConfigMerchant | undefined): void => {
    merchantPreview.value = item;
  };

  const updateMerchant = (item: IAppConfigMerchant): void => {
    merchant.value = item;
  };

  const checkQueryMerchant = (queryMerchant?: LocationQuery[string]): void => {
    if (!queryMerchant && merchant.value) {
      return;
    }

    const merchantItem = queryMerchant && isString(queryMerchant)
      ? findMerchantByValue(queryMerchant)
      : undefined;

    updateMerchant(merchantItem ?? merchantList.value[0]);
  };

  function updateThemeColors(headerColor?: string, buttonsColor?: string): void {
    setThemeColors(
      currentMerchant.value?.header_color || headerColor || config.value?.header_color,
      currentMerchant.value?.buttons_color || buttonsColor || config.value?.buttons_color,
    );
  }

  watch(currentMerchant, (newValue) => {
    setMerchantId(newValue?.id);
    updateThemeColors();
  }, { immediate: true, deep: true });

  watch(merchant, () => {
    merchantPreview.value = undefined;
  });

  watch(browserTitle, (newTitle) => {
    setTitle(newTitle);
  });

  return {
    merchant: currentMerchant,
    merchantList,
    updateMerchant,
    updateMerchantPreview,
    checkQueryMerchant,
    updateThemeColors,
    findMerchantByValue,
  };
});

export default useMerchantStore;
